// The bee inspect panel's pure presentation model (bee-inspect-panel; `client-colony`
// "Tapping a bee opens the inspect panel"). No React, no store, no DOM — the whole
// close-tier-record → panel-copy mapping in one testable function, following the
// `leaderboard-model.ts`/`version-model.ts` precedent.
//
// The point of extracting it is HONESTY about the wire. The canonical observation model
// carries the full fifteen-variant action with target payloads, `state_name`, `food` and
// `age` — but the close-tier LOD projection (PROTOCOL §4) coarsens all of that away. What
// actually arrives per bee is: id, position, facing, a four-value `ActionClass`, `colonyId`,
// `typeId`, and health/nectar quantized to a byte. So:
//
//   - `action` reads as a coarse class ("Foraging"), never as a B++ action or state name;
//   - there is NO per-bee target on the close tier. The one target-bearing close-tier record
//     is `DanceRecord` (keyed by `dancerId`), so "target" resolves to the bee's dance target
//     while it is dancing and otherwise reads "Not reported" — never a stale last-known point;
//   - an enemy's `typeId` indexes ITS colony's private type list (DESIGN §5 — type names are
//     colony-private and never cross the wire), so it can never be named, only counted.

import { ActionClass, type BeeRecord, type DanceRecord } from '../../net/wire/messages';
import { colonyLabel, colonyPalette } from '../match/colony-visuals';

/** One entry of the viewer's OWN colony type roster (`ColonyMeta.types` + the screen's own
 * `colorForType`), the only type list whose names are knowable. */
export interface InspectTypeRef {
  typeId: number;
  name: string;
  colour: string;
}

export interface BeeInspectContext {
  /** The viewer's own colony id (`store.colonyId`), or `null` pre-handshake / for a
   * spectator — in which case every bee reads as another colony's. */
  ownColonyId: number | null;
  /** The own colony's type roster; empty before `ColonyMeta` arrives. */
  ownTypes: readonly InspectTypeRef[];
  /** This bee's active dance in the current frame (`WorldSnapshot.dances.get(id)`), or null. */
  dance: DanceRecord | null;
}

export interface LiveBeeInspect {
  status: 'live';
  id: number;
  isOwnColony: boolean;
  colonyLabel: string;
  typeLabel: string;
  typeColour: string;
  /** Health as a fraction of THIS bee's own max health (the wire byte ÷ 255) — never
   * divided by a balance constant, since a ×2-buffed bee also reads 255 at full. */
  healthFraction: number;
  /** Carried nectar as a fraction of THIS bee's own capacity (the wire byte ÷ 255). */
  nectarFraction: number;
  actionLabel: string;
  isDancing: boolean;
  targetPoint: [number, number] | null;
  targetLabel: string;
}

/** The selected bee is gone from the accepted frame. The wire carries no death signal — a
 * close-tier deletion is identical whether the bee died, left the viewport, or was dropped
 * by close-cap truncation churn (UI.md §8) — so the panel says exactly that much and no
 * more, rather than freezing on the last frame's numbers. */
export interface LostBeeInspect {
  status: 'lost';
  id: number;
}

export type BeeInspectModel = LiveBeeInspect | LostBeeInspect;

const ACTION_LABELS: Record<ActionClass, string> = {
  [ActionClass.Forage]: 'Foraging',
  [ActionClass.Build]: 'Building',
  [ActionClass.Fight]: 'Fighting',
  [ActionClass.Idle]: 'Idle',
};

/** The quantization denominator for the wire's `health`/`nectar` bytes (255 = full). */
const QUANT_MAX = 255;

export function beeInspectModel(
  id: number,
  bee: BeeRecord | undefined,
  ctx: BeeInspectContext,
): BeeInspectModel {
  if (bee === undefined) {
    return { status: 'lost', id };
  }
  const isOwnColony = ctx.ownColonyId !== null && bee.colonyId === ctx.ownColonyId;
  const ownType = isOwnColony ? ctx.ownTypes.find((t) => t.typeId === bee.typeId) : undefined;

  const typeLabel =
    bee.typeId === null
      ? 'Queen'
      : isOwnColony
        ? (ownType?.name ?? `Type ${bee.typeId}`)
        : 'Unknown type';

  const dance = ctx.dance;
  return {
    status: 'live',
    id,
    isOwnColony,
    colonyLabel: colonyLabel(bee.colonyId, ctx.ownColonyId),
    typeLabel,
    typeColour: ownType?.colour ?? colonyPalette(bee.colonyId),
    healthFraction: bee.health / QUANT_MAX,
    nectarFraction: bee.nectar / QUANT_MAX,
    actionLabel: ACTION_LABELS[bee.action] ?? 'Idle',
    isDancing: dance !== null,
    targetPoint: dance === null ? null : [dance.target[0], dance.target[1]],
    targetLabel:
      dance === null ? 'Not reported' : `Dance → ${Math.round(dance.target[0])}, ${Math.round(dance.target[1])}`,
  };
}
